import React, { useEffect, useContext, useMemo, useRef, useState } from 'react';
import { useMsal } from "@azure/msal-react";
import AdminContext from '../../../Context/adminContext';
import Topbar from "../../../Components/Topbar/Topbar";
import './Invoicing.css';
import Table from '../../../Components/Table/TableV2';
import { Box, CircularProgress, Button } from '@mui/material';
import EditDiv from '../../../Components/EditDiv/EditDiv';
import InvSummaryTable from '../../../Components/InvoiceSummaryTable/InvSummaryTable';
import BillingForecast from '../../../Sections/invoicing/BillingForecast';
import { useDispatch, useSelector } from "../../../redux/store";
import {
  getInvSummaryCards,
  fetchMonthBillBreakdownExport,
  getInvSummaryTable,
  getBillingForecast,
} from "../../../redux/slices/invoice";
import Dropdown from '../../../Components/DropDownBox/Dropdown';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportButtonSx } from '../../../GlobalStyles/buttonStyles';
import {
  completeInvoiceRequest,
  fetchInvoicingDashboardPageData,
  updateInvoicingInformation,
} from '../../../redux/slices/invoicingDashboard';

export const TopbarData = [
  {
    title: "Personal",
    link: "/dashboard/personal",
    disabled: false,
  },
  {
    title: "Opportunities",
    link: "/dashboard/opportunities",
    disabled: false,
  },
  {
    title: "Resourcing",
    link: "/dashboard/resourcing",
    disabled: false,
  },
  {
    title: "Invoicing",
    link: "/dashboard/invoicing",
    disabled: false,
  },
];

function Invoicing() {
  const dispatch = useDispatch();
  const { accounts } = useMsal();
  const account = accounts[0];

  const admin = useContext(AdminContext);

  const [reload, setReload] = useState(0);
  const [displayEditDiv, setDisplayEditDiv] = useState(false);
  const [isResponseShown, setIsResponseShown] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [currentData, setCurrentData] = useState(null);
  const [editType, setEditType] = useState('UPDATE');
  const [rowIndex, setRowIndex] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const { invSummaryTableData, invSummaryCardData, invbillingforecast, isinvSummaryCardLoading, isinvSummaryTableLoading, error } = useSelector((state) => state.invoice);
  const invoicingDashboard = useSelector((state) => state.invoicingDashboard);

  const [selectedMonth, setSelectedMonth] = useState('This Month'); // State for selected month
  const [summaryExportLoading, setSummaryExportLoading] = useState(false);
  const pageDataPromiseRef = useRef(null);

  const fetchedData = useMemo(() => invoicingDashboard?.pageData || {}, [invoicingDashboard?.pageData]);
  const loading = Boolean(invoicingDashboard?.loading);
  const teams = fetchedData?.teams && typeof fetchedData.teams === 'object' ? fetchedData.teams : {};

  const monthOptions = [
    'Last Month',
    'This Month',
    'Next Month',
    'On Hold',
    'Unscheduled',
  ];

  useEffect(() => {
    dispatch(getInvSummaryTable(selectedMonth));
    dispatch(getInvSummaryCards(selectedMonth));
  }, [dispatch, selectedMonth]);

  useEffect(() => {
    dispatch(getBillingForecast(year));
  }, [dispatch, year]);

  const handleEdit = (data) => () => {
    setCurrentData(data);
    setDisplayEditDiv(true);
  };

  const handleRowEdit = (data, SQLid, index) => () => {
    let newData = { ...data };
    newData.DataEdit = newData.DataEdit.map((item, i) => ({
      ...item,
      dataName: newData.headers[i + 1],
      data: newData.rows[index][i + 1],
    }));
    setEditType('UPDATE');
    setRowIndex(SQLid);
    handleEdit(newData)();
  };

  const handleUpdateClick = async (DataTitle, selectedValues) => {
    try {
      const data = await dispatch(updateInvoicingInformation({
        actionType: editType,
        sectionName: DataTitle,
        updatedValues: selectedValues,
        rowId: rowIndex,
      })).unwrap();
      setReload((r) => r + 1);
      setApiResponse(data?.message || 'Update completed.');
    } catch (error) {
      console.error("Error in Supplier Info Update:", error);
      setApiResponse('Error updating invoice (please try again).');
    } finally {
      setIsResponseShown(true);
    }
  };

  const handleDoneClick = async (id) => {
    try {
      const data = await dispatch(completeInvoiceRequest({ id })).unwrap();
      setReload((r) => r + 1);
      setApiResponse(data?.message || 'Marked as complete.');
    } catch (error) {
      console.error("Error in Supplier Info Update:", error);
      setApiResponse('Error completing invoice request (please try again).');
    } finally {
      setIsResponseShown(true);
    }
  };

  const handleSummaryExportClick = async () => {
    setSummaryExportLoading(true);
    try {
      await dispatch(fetchMonthBillBreakdownExport());
    } catch (error) {
      console.error("Summary export failed:", error);
    } finally {
      setSummaryExportLoading(false);
    }
  };

  useEffect(() => {
    pageDataPromiseRef.current?.abort?.();
    pageDataPromiseRef.current = dispatch(fetchInvoicingDashboardPageData());
    return () => pageDataPromiseRef.current?.abort?.();
  }, [dispatch, reload]);

  return (
    <>
      <div className='InvoicingMainContent'>
        <div className='dashboardInvoicing'>
          <Topbar TopbarData={TopbarData} />
        </div>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(90% - 80px)' }}>
            <CircularProgress size={65} />
          </Box>
        ) : (
          <React.Fragment>
            <div className='row'>
              <div className='column'>
                <div className='InvoicingTitle'>Billing Forecast</div>
                <BillingForecast
                  data={invbillingforecast}
                  onYearChange={setYear}
                />

                <div className='InvoicingTitle'>Invoices To Raise</div>
                {
                  Object.keys(teams).length === 0 ?
                    <div className='divNoInfo'>No outstanding invoices to be raised.</div>
                    :
                    Object.keys(teams).map(teamName => (
                      <div className='TeamSection' key={teamName}>
                        <div className='InvoicingSubTitle'>{teamName}</div>
                        <div className='InvoicingTableLayout'>
                          <Table data={teams[teamName]} handleRowEdit={handleRowEdit} handleButtonClick={handleDoneClick} />
                        </div>
                      </div>
                    ))}
              </div>
              <div className='column'>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className='InvoicingTitle SummaryTitle'>Monthly Invoice Summary</div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div className='dropdownContainer'>
                      <Dropdown
                        options={monthOptions}
                        initialSelectedValue={selectedMonth}
                        onValueChange={(value) => setSelectedMonth(value)}
                        disabled={loading}
                      />
                    </div>
                    {
                      admin &&
                      <Button
                        variant="contained"
                        onClick={handleSummaryExportClick}
                        disabled={loading || summaryExportLoading}
                        startIcon={<FileDownloadIcon sx={{ fontSize: '16px' }} />}
                        sx={{ ...exportButtonSx, marginLeft: '10px' }}
                      >
                        {summaryExportLoading ? 'Exporting...' : 'Export Summary'}
                      </Button>
                    }
                  </div>
                </div>

                <div className='Container'>
                  <div className='card-container'>
                    <div className="card-content">
                      <div className="card-title">Planned</div>
                      <div className="card-amount">{`£${Number(invSummaryCardData.planned).toLocaleString('en-GB', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}`}</div>
                    </div>
                  </div>
                  <div className='card-container'>
                    <div className="card-content">
                      <div className="card-title">Invoiced</div>
                      <div className="card-amount">{`£${Number(invSummaryCardData.invoiced).toLocaleString('en-GB', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}`}</div>
                    </div>
                  </div>
                </div>

                <div className='invSummaryTable'>
                  <InvSummaryTable jsonData={invSummaryTableData} clickableRowAdd={'/Projects/ProjectProfile'} />
                </div>
              </div>
            </div>
          </React.Fragment>
        )}
      </div>

      {isResponseShown && (
        <div className='InvoicingResponseOverlay'>
          <div className='InvoicingResponseBox'>
            <div
              style={{
                fontSize: 16,
                fontFamily: 'PT Sans',
                textAlign: 'center',
                margin: '20px',
                marginLeft: '25px',
                marginRight: '25px',
              }}
              dangerouslySetInnerHTML={{ __html: apiResponse }}
            />
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div
                className='InvoicingResponseButton'
                onClick={() => setIsResponseShown(false)}
              >
                OK
              </div>
            </div>
          </div>
        </div>
      )}

      {displayEditDiv && <EditDiv data={currentData} handleUpdateClick={handleUpdateClick} setDisplayEditDiv={setDisplayEditDiv} actionType={editType} />}
    </>
  );
}

export default Invoicing;